import { forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, InputLabel, Stack } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  Calendar,
  PuiTextField,
  useFields,
  type ValidateHandle,
} from '@pbt/pbt-ui-components'

import type { AttachmentGroupDetails } from '~/types'

const useStyles = makeStyles(
  (theme) => ({
    descriptionLabel: {
      color: theme.colors.primaryText,
      fontSize: '1.4rem',
      fontWeight: 500,
    },
  }),
  { name: 'MultiAttachmentDetails' },
)

export interface MultiAttachmentHistoryDetailsProps {
  initialDate?: string
  initialSource?: string
  initialTitle?: string
  showAttachmentSource?: boolean
}

export interface MultiAttachmentDetailsHandle extends ValidateHandle {
  getDetails: () => AttachmentGroupDetails
}

const MultiAttachmentDetails = forwardRef<
  ValidateHandle,
  MultiAttachmentHistoryDetailsProps
>(function AttachmentDetails(
  { initialDate, initialTitle, initialSource, showAttachmentSource = true },
  ref,
) {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'MedicalHistory'])

  const { fields, validate } = useFields(
    [
      {
        name: 'date',
        label: t('Common:DATE_TIME'),
        validators: ['required'],
        initialValue: initialDate || new Date().toISOString(),
      },
      {
        name: 'title',
        label: t('Common:TITLE'),
        initialValue: initialTitle || '',
      },
      {
        name: 'diagnosedIn',
        label: t('MedicalHistory:ATTACHMENT_HISTORY_DETAILS.ATTACHMENT_SOURCE'),
        initialValue: initialSource || '',
      },
      { name: 'description', label: t('Common:DESCRIPTION'), initialValue: '' },
    ],
    false,
  )

  const { date, title, diagnosedIn, description } = fields

  const getDetails = () =>
    showAttachmentSource
      ? // @ts-ignore
        R.pluck('value', fields)
      : // @ts-ignore
        R.pluck('value', R.omit(['diagnosedIn'], fields))

  useImperativeHandle(ref, () => ({
    validate,
    getDetails,
  }))

  return (
    <Grid container item direction="column">
      <Grid item>
        <PuiTextField
          autoFocus
          field={title}
          inputProps={{ maxLength: 100 }}
          label={title.label}
        />
      </Grid>
      <Stack alignItems="center" direction="row" spacing={2}>
        <Grid item>
          <Calendar fullWidth field={date} label={`${date.label}*`} />
        </Grid>
        {showAttachmentSource && (
          <Grid item xs>
            <PuiTextField
              field={diagnosedIn}
              inputProps={{ maxLength: 100 }}
              label={diagnosedIn.label}
            />
          </Grid>
        )}
      </Stack>
      <Grid item mt={1.5}>
        <InputLabel htmlFor="description-input">
          <span className={classes.descriptionLabel}>{description.label}:</span>
        </InputLabel>
        <PuiTextField
          multiline
          field={description}
          id="description-input"
          inputProps={{ maxLength: 1000 }}
          minRows={3}
          variant="outlined"
        />
      </Grid>
    </Grid>
  )
})

export default MultiAttachmentDetails
